import React from 'react';
import {
  GitMerge, ShieldCheck, Zap, Layers, RefreshCw, Sparkles,
  Wallet, TrendingUp, Trash2, BarChart2, Bug, Settings, Link2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../hooks/useSEO';

interface ChangelogFeature {
  title: string;
  description: string;
  icon: React.ReactNode;
  type?: 'feature' | 'fix' | 'improvement';
}

interface ChangelogItem {
  version: string;
  date: string;
  badge: 'LATEST' | 'STABLE' | 'LEGACY';
  tagline: string;
  features: ChangelogFeature[];
}

const badgeStyle: Record<string, string> = {
  LATEST:  'bg-[#E50914]/10 border border-[#E50914]/25 text-[#E50914]',
  STABLE:  'bg-neutral-900 border border-neutral-800 text-neutral-500',
  LEGACY:  'bg-neutral-900 border border-neutral-800 text-neutral-600',
};

const typeTag: Record<string, string> = {
  feature:     'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  fix:         'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  improvement: 'bg-green-500/10 text-green-400 border border-green-500/20',
};

const typeLabel: Record<string, string> = {
  feature:     'NEW',
  fix:         'FIX',
  improvement: 'IMPROVED',
};

const ChangelogPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const isDarkMode = !document.documentElement.classList.contains('light');

  useSEO({
    title: 'Changelog',
    description: isId
      ? 'Ikuti terus perkembangan produk kami. Fitur baru, perbaikan bug, dan peningkatan performa — semua tercatat di sini.'
      : 'Follow our product updates. New features, bug fixes, and performance improvements — all recorded here.',
    keywords: 'changelog, release notes, catatcrypto updates, product changelog',
  });

  const changelogData: ChangelogItem[] = [
    {
      version: 'v1.2.0',
      date: isId ? '16 Juni 2026' : 'June 16, 2026',
      badge: 'LATEST',
      tagline: isId
        ? 'Live Wallet Balance Fetching, Selective Account Switcher Syncing, & Dynamic SEO Prerendering.'
        : 'Live Wallet Balance Fetching, Selective Account Switcher Syncing, & Dynamic SEO Prerendering.',
      features: [
        {
          type: 'feature',
          title: isId ? 'Live On-Chain Wallet Balance' : 'Live On-Chain Wallet Balance',
          description: isId
            ? 'Fetch saldo wallet on-chain secara live. Mendukung EVM (via GoldRush/Etherscan), Solana, dan Bitcoin (via mempool.space) dengan konversi kurs USD otomatis via Binance feed.'
            : 'Fetch live on-chain wallet balances. Supports EVM (via GoldRush/Etherscan), Solana, and Bitcoin (via mempool.space) with automatic USD conversion via Binance price feeds.',
          icon: <Wallet className="text-[#E50914]" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Selective Sync & Switcher Filter' : 'Selective Sync & Switcher Filter',
          description: isId
            ? 'Sinkronisasi CEX dan Wallet kini terpisah dan spesifik. Memilih wallet di switcher hanya akan men-sync wallet tersebut. Menghilangkan auto-select CEX jika wallet aktif.'
            : 'CEX and Wallet sync is now isolated and selective. Selecting a wallet in the switcher syncs only that specific wallet account. Bypasses CEX auto-selection if a wallet is active.',
          icon: <RefreshCw className="text-purple-400" size={15} />,
        },
        {
          type: 'improvement',
          title: isId ? 'Dynamic SEO Meta per Halaman' : 'Dynamic SEO Meta per Page',
          description: isId
            ? 'Meningkatkan optimasi SEO dengan tag meta dinamis (Title, Description, Keywords, Canonical URL, og:url, dan skema JSON-LD terstruktur) yang unik untuk setiap halaman publik.'
            : 'Boosted SEO optimization with dynamic meta tags (Title, Description, Keywords, Canonical URL, og:url, and structured JSON-LD organization schema) unique for every public page.',
          icon: <Sparkles className="text-yellow-400" size={15} />,
        },
        {
          type: 'improvement',
          title: isId ? 'Static HTML Route Prerendering' : 'Static HTML Route Prerendering',
          description: isId
            ? 'Konfigurasi post-build prerendering untuk generate static HTML routes (/about, /contact, /pricing, /faq) lengkap dengan meta-header yang crawler-ready.'
            : 'Post-build prerendering configuration to automatically generate static HTML routes (/about, /contact, /pricing, /faq) pre-compiled with crawler-ready meta-headers.',
          icon: <Layers className="text-blue-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Tabrakan Cache Identifikasi Akun' : 'Fix Account ID Cache Collision',
          description: isId
            ? 'Mengubah data cache sinkronisasi menjadi string key unik (misal wallet-5, cex-5) untuk mencegah bentrokan data CEX dan wallet dengan ID database yang sama.'
            : 'Converted local sync cache references to unified string keys (e.g. wallet-5, cex-5) to prevent dashboard/report data clashes between wallets and CEXs with identical database IDs.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
      ]
    },
    {
      version: 'v1.1.0',
      date: isId ? '10 Juni 2026' : 'June 10, 2026',
      badge: 'STABLE',
      tagline: isId
        ? 'On-Chain Wallet Tracking, DEX Trade Journal, Perbaikan Bug, & Peningkatan UX.'
        : 'On-Chain Wallet Tracking, DEX Trade Journal, Bug Fixes & UX Improvements.',
      features: [
        {
          type: 'feature',
          title: isId ? 'On-Chain Wallet Connect' : 'On-Chain Wallet Connect',
          description: isId
            ? 'Hubungkan wallet crypto Anda (MetaMask, Phantom, dll) via WalletConnect atau paste alamat manual. Mendukung EVM (ETH, BSC, Polygon, Arbitrum, Base, Optimism, Avalanche), Solana, dan Bitcoin.'
            : 'Connect your crypto wallet (MetaMask, Phantom, etc.) via WalletConnect or paste address manually. Supports EVM (ETH, BSC, Polygon, Arbitrum, Base, Optimism, Avalanche), Solana, and Bitcoin.',
          icon: <Wallet className="text-purple-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'DEX Trade Journal' : 'DEX Trade Journal',
          description: isId
            ? 'Catat trade dari DEX (Uniswap, Raydium, dll) dengan chart DexScreener real-time. Cari token by nama/ticker atau Contract Address. Deteksi otomatis token duplikat (misal PEPE palsu vs asli).'
            : 'Log trades from DEX (Uniswap, Raydium, etc.) with real-time DexScreener chart. Search token by name/ticker or Contract Address. Auto-detects duplicate tokens (e.g. fake vs real PEPE).',
          icon: <BarChart2 className="text-purple-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Live Unrealized PnL untuk Posisi OPEN' : 'Live Unrealized PnL for OPEN Positions',
          description: isId
            ? 'Posisi DEX yang masih OPEN otomatis fetch harga real-time dari DexScreener setiap 30 detik dan menampilkan unrealized P&L yang terus update.'
            : 'OPEN DEX positions automatically fetch live price from DexScreener every 30s and display continuously updated unrealized P&L.',
          icon: <TrendingUp className="text-green-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Hapus Trade Manual' : 'Delete Manual Trade',
          description: isId
            ? 'Tombol hapus (trash icon) kini tersedia di setiap baris trade manual. Inline confirm sebelum dihapus permanen.'
            : 'Delete button (trash icon) is now available on every manual trade row. Inline confirm before permanent deletion.',
          icon: <Trash2 className="text-red-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Settings → Tab Wallets & Manage Networks' : 'Settings → Wallets Tab & Manage Networks',
          description: isId
            ? 'Tab Wallets baru di Settings. Lihat semua wallet yang terhubung dikelompokkan per address. Tambah jaringan baru ke address EVM yang sama tanpa perlu reconnect.'
            : 'New Wallets tab in Settings. View all connected wallets grouped by address. Add new networks to the same EVM address without reconnecting.',
          icon: <Settings className="text-neutral-400" size={15} />,
        },
        {
          type: 'improvement',
          title: isId ? 'Animasi About Us (Web3 Style)' : 'About Us Animations (Web3 Style)',
          description: isId
            ? 'Animasi scroll-triggered bergaya Web3 di halaman About Us. Bagian "Discipline beats prediction" kini memiliki animasi warna merah yang cycling dari atas ke bawah sebelum settle di tengah.'
            : 'Scroll-triggered Web3-style animations on the About Us page. "Discipline beats prediction" section now has a red color cycling animation from top to bottom before settling in the middle.',
          icon: <Sparkles className="text-yellow-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Scroll Lenis di CexAccountSwitcher' : 'Fix Lenis Scroll in CexAccountSwitcher',
          description: isId
            ? 'Scroll halaman tidak lagi terblokir saat dropdown akun terbuka. Cursor-aware: scroll di dalam dropdown = native scroll, di luar = Lenis normal.'
            : 'Page scroll no longer blocked when account dropdown is open. Cursor-aware: scrolling inside dropdown = native scroll, outside = normal Lenis.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Modal WalletConnect Overlap' : 'Fix WalletConnect Modal Overlap',
          description: isId
            ? 'Modal pilih metode wallet tidak lagi menutupi popup WalletConnect. Modal kita otomatis disembunyikan sebelum Reown modal muncul.'
            : 'Wallet method picker modal no longer overlaps the WalletConnect popup. Our modal is instantly hidden before the Reown modal appears.',
          icon: <Link2 className="text-amber-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Perhitungan Net PnL DEX Trade' : 'Fix DEX Trade Net PnL Calculation',
          description: isId
            ? 'Trade DEX yang OPEN tidak lagi menyimpan PnL negatif (biaya beli). PnL disimpan null untuk posisi terbuka dan dihitung dari harga live. Direction DEX kini BUY/SELL, bukan LONG/SHORT.'
            : 'OPEN DEX trades no longer store negative PnL (buy cost). PnL stored as null for open positions and calculated from live price. DEX direction is now BUY/SELL, not LONG/SHORT.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Chart DEX: DexScreener bukan TradingView' : 'Fix DEX Chart: DexScreener not TradingView',
          description: isId
            ? 'Detail trade DEX kini menampilkan chart DexScreener (dengan pair address exact). Trade CEX tetap menggunakan TradingView.'
            : 'DEX trade detail now shows DexScreener chart (with exact pair address). CEX trades continue using TradingView.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
        {
          type: 'fix',
          title: isId ? 'Fix Redirect Setelah Pembayaran' : 'Fix Post-Payment Redirect',
          description: isId
            ? 'URL redirect setelah pembayaran berhasil kini selalu mengarah ke halaman yang benar sesuai environment. Sebelumnya ada kasus user diarahkan ke halaman yang salah setelah checkout.'
            : 'Post-payment redirect now correctly routes users to the right page depending on environment. Previously, some users were redirected to the wrong page after checkout.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
      ],
    },
    {
      version: 'v1.0.2',
      date: isId ? '9 Juni 2026' : 'June 9, 2026',
      badge: 'STABLE',
      tagline: isId
        ? 'Perbaikan pipeline CI/CD dan stabilisasi deployment.'
        : 'CI/CD pipeline fixes and deployment stabilization.',
      features: [
        {
          type: 'fix',
          title: isId ? 'Stabilisasi Deployment' : 'Deployment Stabilization',
          description: isId
            ? 'Beberapa perbaikan internal pada proses deployment untuk memastikan update berjalan mulus tanpa downtime atau konflik dependency.'
            : 'Several internal fixes to the deployment process to ensure updates roll out smoothly without downtime or dependency conflicts.',
          icon: <Bug className="text-amber-400" size={15} />,
        },
      ],
    },
    {
      version: 'v1.0.1',
      date: isId ? '8 Juni 2026' : 'June 8, 2026',
      badge: 'STABLE',
      tagline: isId
        ? 'Tambah/hapus CEX account di switcher, perbaikan UX dropdown.'
        : 'Add/remove CEX account in switcher, dropdown UX improvements.',
      features: [
        {
          type: 'feature',
          title: isId ? 'Remove Account di CexAccountSwitcher' : 'Remove Account in CexAccountSwitcher',
          description: isId
            ? 'Tombol hapus (trash) per akun di dropdown CexAccountSwitcher. Inline confirm Yes/No tanpa dialog browser. Auto-switch ke akun berikutnya setelah dihapus.'
            : 'Delete button (trash) per account in the CexAccountSwitcher dropdown. Inline Yes/No confirm without browser dialog. Auto-switches to next account after deletion.',
          icon: <Trash2 className="text-red-400" size={15} />,
        },
      ],
    },
    {
      version: 'v1.0.0',
      date: isId ? '7 Juni 2026' : 'June 7, 2026',
      badge: 'LEGACY',
      tagline: isId
        ? 'Peluncuran perdana platform Catat Crypto.'
        : 'Initial launch of the Catat Crypto platform.',
      features: [
        {
          type: 'feature',
          title: isId ? 'Dashboard & Portfolio Analytics' : 'Dashboard & Portfolio Analytics',
          description: isId
            ? 'Dashboard utama dengan Equity Curve, Win Rate, Daily P&L, Active Deployments, dan Catat Crypto Score.'
            : 'Main dashboard with Equity Curve, Win Rate, Daily P&L, Active Deployments, and Catat Crypto Score.',
          icon: <BarChart2 className="text-blue-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Multi-CEX Auto Sync' : 'Multi-CEX Auto Sync',
          description: isId
            ? 'Sinkronisasi otomatis via API key (read-only) untuk Binance, Bybit, OKX, MEXC, Bitget, Indodax, Tokocrypto.'
            : 'Automatic sync via read-only API key for Binance, Bybit, OKX, MEXC, Bitget, Indodax, Tokocrypto.',
          icon: <RefreshCw className="text-amber-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Manual Trade Journal' : 'Manual Trade Journal',
          description: isId
            ? 'Tambah trade manual dengan SPOT/FUTURE, LONG/SHORT, leverage, setup analysis, confidence/emotional sliders.'
            : 'Add manual trades with SPOT/FUTURE, LONG/SHORT, leverage, setup analysis, confidence/emotional sliders.',
          icon: <Zap className="text-[#E50914]" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Settings Modal (Profile, Security, Subscription)' : 'Settings Modal (Profile, Security, Subscription)',
          description: isId
            ? 'Kelola profil, ubah password, aktifkan 2FA, pantau subscription, dan lihat log aktivitas akun.'
            : 'Manage profile, change password, enable 2FA, monitor subscription, and view account activity log.',
          icon: <ShieldCheck className="text-green-400" size={15} />,
        },
        {
          type: 'feature',
          title: isId ? 'Notebook & Trade Journal Harian' : 'Notebook & Daily Trade Journal',
          description: isId
            ? 'Jurnal harian dengan mood tracker, screenshot upload, dan catatan per tanggal di kalender.'
            : 'Daily journal with mood tracker, screenshot upload, and per-date notes on a calendar.',
          icon: <Layers className="text-purple-400" size={15} />,
        },
      ],
    },
  ];

  const name = localStorage.getItem('trader_name') || 'Verified Trader';
  const plan = localStorage.getItem('user_form_data')
    ? JSON.parse(localStorage.getItem('user_form_data') || '{}').plan || 'free'
    : 'free';

  return (
    <div className={`min-h-screen flex font-sans ${isDarkMode ? 'bg-black text-white' : 'bg-[#FAFAFA] text-black'}`}>
      {/* Sidebar hidden on Changelog page */}

      <main className={`flex-1 min-h-screen flex flex-col ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#FAFAFA]'}`}>
        <div className="flex-1">
          {/* Page header */}
          <div className={`px-8 py-10 relative overflow-hidden ${
            isDarkMode 
              ? 'border-b border-neutral-900 bg-[#080808]' 
              : 'border-b border-gray-200 bg-white'
          }`}>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-1/4 w-[400px] h-[180px] rounded-full bg-[#E50914]/4 blur-3xl" />
            </div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E50914]/10 border border-[#E50914]/20 text-[9px] font-black uppercase tracking-widest text-[#E50914] mb-3">
                <GitMerge size={9} />
                What's New
              </div>
              <h1 className={`text-2xl font-black tracking-tight mb-1.5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                Release Notes
              </h1>
              <p className={`text-xs max-w-md leading-relaxed ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                {isId
                  ? 'Ikuti terus perkembangan produk kami. Fitur baru, perbaikan bug, dan peningkatan performa — semua tercatat di sini.'
                  : 'Follow our product updates. New features, bug fixes, and performance improvements — all recorded here.'}
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="px-8 py-8 max-w-[860px]">
            <div className={`relative ml-2 space-y-10 pb-12 ${
              isDarkMode ? 'border-l border-neutral-900' : 'border-l border-gray-200'
            }`}>
              {changelogData.map((item, idx) => (
                <div key={item.version} className="relative pl-8 group">
                  {/* Timeline node */}
                  <div className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full transition-all ${
                    isDarkMode ? 'border-2 border-[#0a0a0a]' : 'border-2 border-[#FAFAFA]'
                  } ${
                    idx === 0
                      ? 'bg-[#E50914] shadow-[0_0_10px_rgba(229,9,20,0.5)]'
                      : isDarkMode ? 'bg-neutral-800' : 'bg-gray-300'
                  }`} />

                  {/* Version + date + badge */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-lg font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      {item.version}
                    </span>
                    <span className={`text-[9px] font-black tracking-widest px-2 py-0.5 rounded font-mono uppercase ${
                      isDarkMode ? 'bg-neutral-900 text-neutral-500' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {item.date}
                    </span>
                    <span className={`text-[9px] font-black tracking-widest px-2 py-0.5 rounded uppercase ${badgeStyle[item.badge]}`}>
                      {item.badge}
                    </span>
                  </div>

                  <p className={`text-xs font-medium leading-relaxed mb-5 ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                    {item.tagline}
                  </p>

                  {/* Feature cards */}
                  <div className="space-y-2.5">
                    {item.features.map(feat => (
                      <div key={feat.title} className={`rounded-xl p-4 flex items-start gap-3.5 transition-colors ${
                        isDarkMode 
                          ? 'bg-neutral-950/60 border border-neutral-900 hover:border-neutral-800' 
                          : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
                      }`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          isDarkMode 
                            ? 'bg-[#111] border border-neutral-900' 
                            : 'bg-gray-100 border border-gray-200'
                        }`}>
                          {feat.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className={`text-[11px] font-black uppercase tracking-wide ${
                              isDarkMode ? 'text-white' : 'text-black'
                            }`}>
                              {feat.title}
                            </p>
                            {feat.type && (
                              <span className={`text-[8px] font-black tracking-widest px-1.5 py-0.5 rounded uppercase ${typeTag[feat.type]}`}>
                                {typeLabel[feat.type]}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                            {feat.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* End node */}
              <div className="relative pl-8">
                <div className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${
                  isDarkMode 
                    ? 'bg-neutral-900 border-2 border-[#0a0a0a]' 
                    : 'bg-gray-300 border-2 border-[#FAFAFA]'
                }`} />
                <p className={`text-[10px] font-bold uppercase tracking-widest pt-1 ${
                  isDarkMode ? 'text-neutral-700' : 'text-gray-400'
                }`}>
                  {isId ? 'Awal platform · 7 Juni 2026' : 'Platform launch · June 7, 2026'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChangelogPage;
